import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, Menu, Spin } from 'antd';
import PropTypes from 'prop-types';
import {
  EditOutlined,
  SaveOutlined,
  DeleteOutlined,
  PlusOutlined,
  CloseOutlined,
  StarOutlined,
  StarFilled,
  ReloadOutlined,
} from '@ant-design/icons';

import OnTraccrButton from '../common/buttons/OnTraccrButton';
import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import useToggle from '../common/hooks/useToggle';
import Permissions from '../auth/Permissions';

import {
  createNewDashboard,
  selectDashboard,
  setDashboardEdit,
  deleteSavedDashboard,
  favoriteDashboard,
} from './state/dashboard.actions';

import colors from '../constants/Colors';

import useViewableCustomExports from '../reports/Exports/useViewableCustomExports';

import Debouncer from '../helpers/Debouncer';
import { createShortList, getShortLists } from '../boards/state/boards.actions';
import SimpleTextInputModal from '../common/modals/SimpleTextInputModal';

const DEFAULT_WIDGETS = [
  { title: 'Clock', id: 'clock' },
  { title: 'Quick Stats', id: 'quickStats' },
  { title: 'Live Feed', id: 'liveFeed' },
  { title: 'Tasks', id: 'tasks' },
];
const FILLED_STAR = <StarFilled style={{ fontSize: 16, color: 'gold' }}/>;
const returnKeyTypeSet = new Set(['shortLists', 'formExports', 'timeTrackingExports', 'boardExports']);
const debouncer = new Debouncer();

export default function DashboardTitle({
  onSave,
  onEditCancel: propEditCancel,
  onAddWidget: propAddWidget,
  onReloadClicked: propOnReload,
  currentLayout = [],
}) {
  const dispatch = useDispatch();
  const {
    id: dashboardId,
    title = 'Dashboard',
    isFavorite,
  } = useSelector(state => state.dashboard.selectedDashboard ?? {});
  const savedDashboards = useSelector(state => state.dashboard.savedDashboards ?? []);
  const edit = useSelector(state => state.dashboard.isEdit);
  const savedReports = useSelector(state => state.analytics.savedReports);
  const [viewableCustomExports] = useViewableCustomExports();
  const shortLists = useSelector((state) => state.boards.shortLists);

  const [currentTitle, setCurrentTitle] = useState(title);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    toggle: toggleAddShortList,
    isToggled: isToggledAddShortList,
  } = useToggle();

  const onEditClicked = (e) => {
    e.stopPropagation();
    dispatch(setDashboardEdit({ isEdit: true }));
    setDropdownVisible(false);
  };

  const onTextChange =(e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setCurrentTitle(value);
  };

  const onDeleteClicked = (e) => {
    e.stopPropagation();
    CustomConfirmModal({
      title: `Delete Dashboard '${currentTitle}' ?`,
      onOk: () => {
        dispatch(deleteSavedDashboard({ id: dashboardId }));
      },
      okText: 'Delete',
    });
  };

  const onSaveClicked = (e) => {
    e.stopPropagation();
    onSave(currentTitle);
  };

  const onEditCancel = (e) => {
    e.stopPropagation();
    setCurrentTitle(title);
    dispatch(setDashboardEdit({ isEdit: false }));
    propEditCancel();
  }

  const onDashboardSelected = ({ key }) => {
    if (key === 'new') {
      dispatch(createNewDashboard());
    } else if (key === 'default') {
      dispatch(selectDashboard({}));
    } else {
      dispatch(selectDashboard({ id: key }));
    }
    setDropdownVisible(false);
  }

  const onAddWidget = ({ key, domEvent: e }) => {
    e?.stopPropagation();
    const [itemType, itemId] = key?.split('-') ?? [];
    if (itemId === 'addShortList') {
      toggleAddShortList();
      return;
    }
    propAddWidget(
      returnKeyTypeSet.has(itemType)
      ? key
      : itemId
    );
  }

  const onFavoriteToggle = (e) => {
    e.stopPropagation();
    dispatch(favoriteDashboard({
      id: dashboardId,
      isFavorite,
    }));
  };

  const onReloadClicked = (e) => {
    e.stopPropagation();
    if (loading) return;
    setLoading(true);
    debouncer.debounce(() => {
      setLoading(false);
      propOnReload();
    }, 1000);
  }

  useEffect(() => {
    setCurrentTitle(title);
  },[title]);

  useEffect(() => {
    dispatch(setDashboardEdit({ isEdit: false }));
    dispatch(getShortLists());
  }, []);

  const titleNode = edit
    ? <OnTraccrTextInput
        autoFocus
        defaultValue={currentTitle}
        onChange={onTextChange}
        style={{ width: 250 }}
        onClick={(e) => e.stopPropagation()}
      />
    : currentTitle;

  const layoutSet = new Set(currentLayout.map((l) => l.i));

  const {
    widgetCount,
    widgets,
  } = useMemo(() => {
    const validDefaults = DEFAULT_WIDGETS.filter((l) => {
      if (layoutSet.has(l.id)) return false;
      if (l.id === 'quickStats' && !Permissions.has('DASHBOARD_QUICK_STATS')) return false;
      if (l.id === 'liveFeed' && !Permissions.has('DASHBOARD_LIVE_FEED')) return false;
      return true;
    });
    let count = validDefaults.length;
    const wGroups = [{
      id: 'default',
      title: 'Default',
      items: validDefaults,
    }, {
      id: 'forms',
      title: 'Forms',
      items: [],
    }, {
      id: 'formExports',
      title: 'Form Exports',
      items: [],
    }, {
      id: 'boards',
      title: 'Boards',
      items: [],
    }, {
      id: 'shortLists',
      title: 'Board Short Lists',
      items: [
        {
          id: 'addShortList',
          title: (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <PlusOutlined/>
              Add Short List
            </div>
          ),
        },
        ...shortLists.map((shortList) => ({
          id: shortList.id,
          title: shortList.name,
        })).filter((shortList) => !layoutSet.has(`shortLists-${shortList.id}`)),
      ],
    }, {
      id: 'timeTrackingExports',
      title: 'Time Tracking Exports',
      items: [],
    }, {
      id: 'timeTrackingAnalytics',
      title: 'Time Tracking Analytics',
      items: [],
    }, {
      id: 'boardExports',
      title: 'Board Cards Exports',
      items: [],
    }];
    if (Permissions.has('MANAGE_ANALYTICS')) {
      Object.values(savedReports).forEach((savedReport) => {
        if (layoutSet.has(savedReport.id)) return;
        if (savedReport.boardId) {
          wGroups[3].items.push(savedReport);
          count += 1;
        } else if (savedReport.formTemplateId) {
          wGroups[1].items.push(savedReport);
          count += 1;
        } else if (savedReport.category === 'timecard') {
          wGroups[6].items.push(savedReport);
          count += 1;
        }
      });
    }
    viewableCustomExports.forEach((customExport) => {
      if (customExport.type === 'forms') {
        if (layoutSet.has(`formExports-${customExport.id}`)) return;
        wGroups[2].items.push(customExport)
        count += 1;
      } else if (customExport.type === 'timeEntries') {
        if (layoutSet.has(`timeTrackingExports-${customExport.id}`)) return;
        wGroups[5].items.push(customExport)
        count += 1;
      } else if (customExport.type === 'boardCards') {
        if (layoutSet.has(`boardExports-${customExport.id}`)) return;
        wGroups[7].items.push(customExport);
        count += 1;
      }
    })
    return {
      widgetCount: count,
      widgets: wGroups.filter((group) => group.items.length > 0),
    }
  }, [layoutSet, savedReports, viewableCustomExports, shortLists]);

  const favoriteIcon = (dashboard) => (
    <span style={{ marginLeft: 5 }}>{
      dashboard.isFavorite
        ? FILLED_STAR
        : <StarOutlined style={{ fontSize: 16 }}/>
      }
    </span>
  );

  const onAddShortList = async (name) => {
    const { shortList: newShortList } = await dispatch(createShortList({ name })) || {};
    if (newShortList) {
      onAddWidget({ key: `shortLists-${newShortList.id}` });
      toggleAddShortList();
    }
  }

  return (
    <>
      <Dropdown
        trigger={['click']}
        overlay={
          <Menu onClick={onDashboardSelected}>
            <Menu.Item key='new'>
              New Dashboard <span><PlusOutlined/></span>
            </Menu.Item>
            <Menu.Item key='default'>
              Default Dashboard
            </Menu.Item>
            {savedDashboards.map((dashboard) => (
                <Menu.Item
                  key={dashboard.id}
                >
                  {dashboard.title}
                  {dashboard.isFavorite
                    ? <span style={{ marginLeft: 5 }}>{FILLED_STAR}</span>
                    : null
                  }
                </Menu.Item>
            ))}
          </Menu>
        }
        visible={dropdownVisible}
        onVisibleChange={setDropdownVisible}
      >
        <span {...edit ? {} : { id: 'board-crumb-button'}}>
          {titleNode}
        </span>
      </Dropdown>
      {!edit && dashboardId &&
        <span className='analytics-title-button' onClick={onFavoriteToggle}>
          {favoriteIcon({ isFavorite })}
        </span>
      }
      {!edit && dashboardId && dashboardId !== 'new' &&
        <span className='analytics-title-button' onClick={onReloadClicked}>
          {loading ? <Spin size='small' style={{ marginTop: 5 }}/> : <ReloadOutlined />}
        </span>
      }
      {!edit && dashboardId && <span className='analytics-title-button' onClick={onEditClicked}>
        <EditOutlined/>
      </span>}
      {!edit && dashboardId && dashboardId !== 'new' && <span className='analytics-title-button' onClick={onDeleteClicked}>
        <DeleteOutlined style={{ color: colors.ONTRACCR_RED }}/>
      </span>}
      {edit && <span className='analytics-title-button' onClick={onEditCancel}>
        <CloseOutlined/>
      </span>}
      {edit && currentTitle.length > 0 && <span className='analytics-title-button' onClick={onSaveClicked}>
        <SaveOutlined/>
      </span>}
      {
        edit && currentLayout.length < 9 && widgetCount > 0 &&
        <span style={{ marginLeft: 40 }}>
          <Dropdown
            trigger={['click']}
            onClick={(e) => e.stopPropagation()}
            overlay={
              <Menu onClick={onAddWidget}>
                {widgets.map((widgetGroup) => (
                    <Menu.SubMenu
                      title={widgetGroup.title}
                      key={widgetGroup.id}
                      popupClassName="ant-dropdown-menu-submenu dashboard-widget-submenu"
                    >
                    {
                      widgetGroup.items.map((widget) => (
                        <Menu.Item
                          key={`${widgetGroup.id}-${widget.id}`}
                          onClick={widget.onClick ? (e) => {
                            widget.onClick(e);
                          } : undefined}
                        >
                          {widget.title}
                        </Menu.Item>
                      ))
                    }
                  </Menu.SubMenu>
                ))}
              </Menu>
            }
          >
            <OnTraccrButton title='Add Widget' icon={<PlusOutlined/>}/>
          </Dropdown>

        </span>
      }
      <SimpleTextInputModal
        title='Add Short List'
        visible={isToggledAddShortList}
        onClose={toggleAddShortList}
        onSave={onAddShortList}
      />
    </>
  );
}

DashboardTitle.propTypes = {
  onSave: PropTypes.func.isRequired,
  onEditCancel: PropTypes.func.isRequired,
  onAddWidget: PropTypes.func.isRequired,
  onReloadClicked: PropTypes.func.isRequired,
  currentLayout: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
};
